import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, UserPlus, X, Save, ArrowRight, Users, ListChecks, GripVertical } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const isSTAEvent = (code) => code === 'STA';
const formatMMSS = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00';
  const totalSec = Math.round(Number(seconds));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
const parseMMSS = (val) => {
  if (val === '' || val === null || val === undefined) return 0;
  const parts = String(val).split(':');
  if (parts.length === 2) {
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  }
  return parseFloat(val) || 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDateInput = (iso) => {
  if (!iso) return todayISO();
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return todayISO(); }
};

/**
 * BulkRegistrationDialog — multi-select athletes, drag & drop into target zone, edit AP + announcement date, bulk save.
 *
 * Props:
 *   - open, onClose
 *   - competitions, freedivers, eventTypes
 *   - onSaved: callback after successful bulk save
 *   - translate
 */
const BulkRegistrationDialog = ({ open, onClose, competitions, freedivers, eventTypes, onSaved, translate }) => {
  const t = (k, fb) => (translate ? translate(k, fb) : fb);

  const [competitionId, setCompetitionId] = useState('');
  const [eventTypeCode, setEventTypeCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [targetItems, setTargetItems] = useState([]); // [{freediver_id, announced_performance, announced_performance_input, registration_date}]
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type, message}
  const [removeMissing, setRemoveMissing] = useState(false);
  const [draggedFdId, setDraggedFdId] = useState(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setCompetitionId('');
      setEventTypeCode('');
      setSearchQuery('');
      setTargetItems([]);
      setFeedback(null);
      setRemoveMissing(false);
    }
  }, [open]);

  // Load existing registrations when comp + event type both selected
  useEffect(() => {
    if (!open || !competitionId || !eventTypeCode) {
      setTargetItems([]);
      return;
    }
    const load = async () => {
      try {
        setLoadingExisting(true);
        const res = await axios.get(`${API_BASE_URL}/api/registrations`, {
          params: { competition_id: competitionId, event_type_code: eventTypeCode },
        });
        const regs = res.data?.registrations || [];
        const items = regs.map((r) => ({
          registration_id: r.id,
          freediver_id: r.freediver_id,
          announced_performance: r.announced_performance,
          announced_performance_input: isSTAEvent(eventTypeCode)
            ? formatMMSS(r.announced_performance)
            : String(r.announced_performance ?? ''),
          registration_date: toDateInput(r.registration_date),
        }));
        setTargetItems(items);
      } catch (e) {
        console.error('Load registrations failed', e);
      } finally {
        setLoadingExisting(false);
      }
    };
    load();
  }, [open, competitionId, eventTypeCode]);

  const targetIds = useMemo(() => new Set(targetItems.map((i) => i.freediver_id)), [targetItems]);

  // Source list: freedivers NOT yet in target, filtered by search
  const sourceList = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return (freedivers || [])
      .filter((fd) => !targetIds.has(fd.id))
      .filter((fd) => {
        if (!q) return true;
        const name = `${fd.first_name || ''} ${fd.last_name || ''}`.toLowerCase();
        return name.includes(q) || (fd.email || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
  }, [freedivers, targetIds, searchQuery]);

  const fdById = useMemo(() => {
    const m = {};
    (freedivers || []).forEach((f) => { m[f.id] = f; });
    return m;
  }, [freedivers]);

  // Add a freediver to target
  const addToTarget = useCallback((fdId) => {
    if (!fdId || targetIds.has(fdId)) return;
    setTargetItems((prev) => [
      ...prev,
      {
        freediver_id: fdId,
        announced_performance: 0,
        announced_performance_input: isSTAEvent(eventTypeCode) ? '' : '',
        registration_date: todayISO(),
      },
    ]);
  }, [targetIds, eventTypeCode]);

  const removeFromTarget = (fdId) => {
    setTargetItems((prev) => prev.filter((i) => i.freediver_id !== fdId));
  };

  const updateTargetItem = (fdId, field, value) => {
    setTargetItems((prev) =>
      prev.map((i) => (i.freediver_id === fdId ? { ...i, [field]: value } : i))
    );
  };

  // Drag & drop handlers
  const handleDragStart = (e, fdId) => {
    setDraggedFdId(fdId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fdId);
  };
  const handleDragEnd = () => {
    setDraggedFdId(null);
    setDropZoneActive(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropZoneActive(true);
  };
  const handleDragLeave = () => setDropZoneActive(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDropZoneActive(false);
    const fdId = e.dataTransfer.getData('text/plain') || draggedFdId;
    if (fdId) addToTarget(fdId);
    setDraggedFdId(null);
  };

  const canSave = competitionId && eventTypeCode && targetItems.length > 0 && !saving;

  const validateTarget = () => {
    const errors = [];
    targetItems.forEach((item) => {
      const fd = fdById[item.freediver_id];
      const name = fd ? `${fd.first_name} ${fd.last_name}` : item.freediver_id;
      const ap = isSTAEvent(eventTypeCode) ? parseMMSS(item.announced_performance_input) : parseFloat(item.announced_performance_input);
      if (!ap || ap <= 0 || isNaN(ap)) {
        errors.push(`${name}: AP invalide`);
      }
    });
    return errors;
  };

  const handleSave = async () => {
    setFeedback(null);
    const errors = validateTarget();
    if (errors.length) {
      setFeedback({ type: 'error', message: errors.join(' • ') });
      return;
    }
    try {
      setSaving(true);
      const items = targetItems.map((i) => ({
        freediver_id: i.freediver_id,
        announced_performance: isSTAEvent(eventTypeCode)
          ? parseMMSS(i.announced_performance_input)
          : parseFloat(i.announced_performance_input),
        registration_date: i.registration_date ? new Date(i.registration_date + 'T00:00:00').toISOString() : null,
      }));
      const res = await axios.post(`${API_BASE_URL}/api/registrations/bulk-upsert`, {
        competition_id: competitionId,
        event_type_code: eventTypeCode,
        items,
        remove_missing: removeMissing,
      });
      setFeedback({
        type: 'success',
        message: `${res.data.created} créées, ${res.data.updated} mises à jour${res.data.deleted ? `, ${res.data.deleted} supprimées` : ''}.`,
      });
      if (onSaved) onSaved();
      // Auto-close after success delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Bulk save failed', err);
      setFeedback({ type: 'error', message: err.response?.data?.detail || 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const eventUnit = isSTAEvent(eventTypeCode) ? 'mm:ss' : (eventTypes.find(e => e.code === eventTypeCode)?.scoring_rule === 'time' ? 'sec' : 'm');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent
        className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-6xl w-[95vw] max-h-[88vh] flex flex-col z-[10000]"
        data-testid="bulk-registration-dialog"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-blue-100 flex items-center gap-2">
            <ListChecks size={20} className="text-cyan-300" />
            {t('bulkRegistration', 'Inscriptions multiples')}
          </DialogTitle>
          <DialogDescription className="text-blue-300 text-xs">
            {t('bulkRegistrationDesc', 'Sélectionnez une compétition et une épreuve, puis glissez-déposez les apnéistes ou utilisez le bouton +.')}
          </DialogDescription>
        </DialogHeader>

        {/* Top selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 shrink-0">
          <div>
            <Label className="text-blue-200 mb-1 block text-xs">{t('competition', 'Compétition')} *</Label>
            <Select value={competitionId} onValueChange={setCompetitionId} disabled={saving}>
              <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100" data-testid="bulk-competition-select">
                <SelectValue placeholder={t('selectCompetition', 'Sélectionner une compétition')} />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {(competitions || [])
                  .filter((c) => !['completed', 'closed', 'cancelled'].includes((c.status || '').toLowerCase()))
                  .map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-blue-200 mb-1 block text-xs">{t('eventType', 'Épreuve')} *</Label>
            <Select value={eventTypeCode} onValueChange={setEventTypeCode} disabled={saving || !competitionId}>
              <SelectTrigger className="bg-blue-950/60 border-blue-700/50 text-blue-100" data-testid="bulk-eventtype-select">
                <SelectValue placeholder={t('selectEventType', "Sélectionner l'épreuve")} />
              </SelectTrigger>
              <SelectContent className="z-[10001]">
                {(eventTypes || []).map((et) => (
                  <SelectItem key={et.id} value={et.code}>{et.code} — {et.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Two columns: target (left) + source (right) */}
        <div className="flex flex-col md:flex-row gap-3 flex-1 min-h-[280px] mt-1 overflow-hidden">
          {/* TARGET — Inscrits */}
          <div
            className={`flex-1 flex flex-col rounded-lg border-2 min-w-0 min-h-[200px] ${dropZoneActive ? 'border-cyan-400 bg-cyan-900/20' : 'border-blue-700/40 bg-blue-950/40'} transition-colors`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="target-zone"
          >
            <div className="p-3 border-b border-blue-800/40 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <ListChecks size={16} className="text-cyan-400" />
                <h3 className="font-semibold text-blue-100">
                  {t('inscritList', 'Inscrits')} ({targetItems.length})
                </h3>
              </div>
              {loadingExisting && <span className="text-xs text-blue-400 animate-pulse">Chargement…</span>}
            </div>
            {!competitionId || !eventTypeCode ? (
              <div className="flex-1 flex items-center justify-center text-blue-400 text-sm p-6 text-center">
                Choisissez d'abord une compétition et une épreuve.
              </div>
            ) : targetItems.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-blue-400 text-sm p-6 text-center">
                <UserPlus size={32} className="mb-2 opacity-50" />
                <p>Glissez-déposez des apnéistes depuis la droite</p>
                <p className="text-xs opacity-70 mt-1">ou cliquez sur "+" sur chaque ligne.</p>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1 p-2 space-y-2 min-h-0" data-testid="target-list">
                {targetItems.map((item) => {
                  const fd = fdById[item.freediver_id] || { first_name: '?', last_name: item.freediver_id, nationality: '', email: '' };
                  return (
                    <div
                      key={item.freediver_id}
                      className="bg-blue-900/40 border border-blue-700/40 rounded-lg p-3 flex flex-col gap-2"
                      data-testid={`target-item-${item.freediver_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-blue-100 font-medium truncate">
                            {fd.first_name} {fd.last_name}
                          </div>
                          <div className="text-xs text-blue-400 truncate">
                            {fd.nationality && <span>{fd.nationality} • </span>}
                            {fd.email || ''}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromTarget(item.freediver_id)}
                          className="text-red-300 hover:bg-red-900/30 h-8 w-8 p-0 shrink-0"
                          title="Retirer"
                          data-testid={`remove-target-${item.freediver_id}`}
                        >
                          <X size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-blue-300 uppercase">AP ({eventUnit})</Label>
                          <Input
                            value={item.announced_performance_input}
                            onChange={(e) => {
                              let v = e.target.value;
                              if (isSTAEvent(eventTypeCode)) {
                                v = v.replace(/[^0-9:]/g, '').slice(0, 5);
                                if (v.length === 2 && !v.includes(':')) v = v + ':';
                              } else {
                                v = v.replace(/[^0-9.]/g, '');
                              }
                              updateTargetItem(item.freediver_id, 'announced_performance_input', v);
                            }}
                            placeholder={isSTAEvent(eventTypeCode) ? 'mm:ss' : 'm'}
                            className="bg-blue-950/60 border-blue-700/50 text-blue-100 h-9 text-sm font-mono"
                            data-testid={`ap-input-${item.freediver_id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-blue-300 uppercase">Date d'annonce</Label>
                          <Input
                            type="date"
                            value={item.registration_date}
                            onChange={(e) => updateTargetItem(item.freediver_id, 'registration_date', e.target.value)}
                            className="bg-blue-950/60 border-blue-700/50 text-blue-100 h-9 text-sm"
                            data-testid={`date-input-${item.freediver_id}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* SOURCE — Apnéistes disponibles */}
          <div className="flex-1 flex flex-col rounded-lg border-2 border-blue-700/40 bg-blue-950/40 min-w-0 min-h-[200px]">
            <div className="p-3 border-b border-blue-800/40 shrink-0 space-y-2">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-cyan-400" />
                <h3 className="font-semibold text-blue-100">
                  {t('availableFreedivers', 'Apnéistes disponibles')} ({sourceList.length})
                </h3>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={14} />
                <Input
                  placeholder={t('searchByName', 'Recherche par nom ou prénom...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-blue-950/60 border-blue-700/50 text-blue-100 h-9"
                  data-testid="source-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-2 space-y-1 min-h-0" data-testid="source-list">
              {sourceList.length === 0 ? (
                <div className="flex items-center justify-center text-blue-400 text-sm p-6 text-center">
                  {searchQuery ? 'Aucun apnéiste trouvé' : 'Tous les apnéistes sont déjà inscrits'}
                </div>
              ) : (
                sourceList.map((fd) => (
                  <div
                    key={fd.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, fd.id)}
                    onDragEnd={handleDragEnd}
                    className={`group bg-blue-900/30 hover:bg-blue-800/40 border border-blue-700/30 rounded-lg p-2 flex items-center gap-2 cursor-grab active:cursor-grabbing transition-all ${draggedFdId === fd.id ? 'opacity-40 scale-95' : ''}`}
                    data-testid={`source-item-${fd.id}`}
                  >
                    <GripVertical size={14} className="text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-blue-100 text-sm font-medium truncate">
                        {fd.first_name} {fd.last_name}
                      </div>
                      <div className="text-xs text-blue-400 truncate">
                        {fd.nationality || ''} {fd.email ? `• ${fd.email}` : ''}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => addToTarget(fd.id)}
                      disabled={!competitionId || !eventTypeCode}
                      className="text-cyan-300 hover:bg-cyan-900/30 h-8 w-8 p-0 shrink-0 opacity-70 hover:opacity-100 transition-opacity"
                      title="Ajouter aux inscrits"
                      data-testid={`add-source-${fd.id}`}
                    >
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-blue-800/40 pt-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-2 text-xs text-blue-300 cursor-pointer">
              <input
                type="checkbox"
                checked={removeMissing}
                onChange={(e) => setRemoveMissing(e.target.checked)}
                disabled={saving}
                data-testid="remove-missing-checkbox"
              />
              <span>Supprimer les inscriptions absentes de cette liste</span>
            </label>
            {feedback && (
              <div
                className={`text-xs px-2 py-1 rounded ${
                  feedback.type === 'success' ? 'bg-green-900/40 text-green-200' : 'bg-red-900/40 text-red-200'
                }`}
                data-testid="bulk-feedback"
              >
                {feedback.message}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} className="border-blue-600/50 text-blue-200">
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 min-w-[200px]"
              data-testid="bulk-save-btn"
            >
              {saving ? (
                <span className="animate-pulse">Enregistrement...</span>
              ) : (
                <>
                  <Save className="mr-2" size={16} />
                  Valider les inscriptions ({targetItems.length})
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkRegistrationDialog;
